import { BarSize } from './bar-size.js';
/**
 * Heavy/light bend threshold: #5 and below = light, #6 and above = heavy.
 */
const HEAVY_BEND_THRESHOLD = 6;
/**
 * Effective bar count for a line. When `quantity` is 0 on a span/spacing-driven
 * function (RA, TC, TA, TB), compute count as floor(span / spacing) + 1.
 */
function computeEffectiveBarCount(line) {
    if (line.quantity > 0)
        return line.quantity * line.multiplier;
    const spanDriven = line.function === 'RA' || line.function === 'TC' ||
        line.function === 'TA' || line.function === 'TB';
    if (spanDriven && line.span && line.spacing && !line.spacing.isZero()) {
        const spanInches = line.span.toDecimalInches();
        const spacingInches = line.spacing.toDecimalInches();
        return (Math.floor(spanInches / spacingInches) + 1) * line.multiplier;
    }
    return line.quantity * line.multiplier;
}
/**
 * Generate weight summary entries grouped by bar size.
 * Lines must have weightLbs already computed (by the rollup engine).
 *
 * The default material filter is 'TR' (Trade Rebar). Pass 'TP' to summarize
 * Plain Steel into the same column shape — both render with identical
 * Size | Total | Straight | HEAVY | LIGHT | Stock Wgt columns per aSa source
 * (see E26-1660 ASA FIELDS.pdf for the plain-steel example).
 *
 * Categories:
 *   - stock: bar length === stock bar length (full-length bars)
 *   - straight: class S, not stock length
 *   - heavy: class L or H, size >= #6
 *   - light: class L or H, size < #6
 */
export function generateWeightSummary(lines, defaults, materialFilter = 'TR') {
    const bySize = new Map();
    for (const line of lines) {
        if (line.material !== materialFilter || !line.size)
            continue;
        const key = line.size.imperial;
        const entry = bySize.get(key) ?? {
            straight: 0, heavy: 0, light: 0, stockWeight: 0,
            barCount: 0, totalLinealFeet: 0, total: 0,
        };
        const count = computeEffectiveBarCount(line);
        const barLengthFt = line.L1 ? line.L1.toDecimalFeet() : 0;
        const isStock = line.L1 !== null && line.L1.equals(defaults.stockBarLength);
        entry.total += line.weightLbs;
        entry.barCount += count;
        entry.totalLinealFeet += count * barLengthFt;
        if (line.class === 'S') {
            if (isStock) {
                entry.stockWeight += line.weightLbs;
            }
            else {
                entry.straight += line.weightLbs;
            }
        }
        else {
            if (line.size.imperial >= HEAVY_BEND_THRESHOLD) {
                entry.heavy += line.weightLbs;
            }
            else {
                entry.light += line.weightLbs;
            }
        }
        bySize.set(key, entry);
    }
    const result = [];
    const sortedKeys = [...bySize.keys()].sort((a, b) => a - b);
    for (const key of sortedKeys) {
        const entry = bySize.get(key);
        result.push({
            size: BarSize.fromImperial(key),
            total: entry.total,
            straight: entry.straight,
            heavy: entry.heavy,
            light: entry.light,
            stockWeight: entry.stockWeight,
            barCount: entry.barCount,
            totalLinealFeet: entry.totalLinealFeet,
            tons: entry.total / 2000,
        });
    }
    return result;
}
